import { useMutation, type UseMutationOptions } from "@tanstack/react-query";

import { authClient, betterAuthErrorText } from "lib/auth/authClient";
import { SKIP_SENTRY_FOR_CLIENT_ERRORS } from "lib/sentryErrors";
import { ALREADY_INVITED_CODE } from "./useSendBetterAuthInvites";

/**
 * What became of an invite. Only `invited` sent one; the rest are refusals the requester can act on
 * rather than failures.
 */
export type CompanyInviteOutcome =
  | "invited"
  | "alreadyInvited"
  | "alreadyMember"
  | "accountElsewhere";

/** Who to invite, and the company role they'll join with. Only a company admin may invite an admin. */
export type CompanyInvite = { email: string; role: "member" | "admin" };

// The org plugin's own codes, plus auth-service's `USER_ACCOUNT_EXISTS_ELSEWHERE`.
const OUTCOME_BY_CODE: Partial<Record<string, CompanyInviteOutcome>> = {
  [ALREADY_INVITED_CODE]: "alreadyInvited",
  USER_IS_ALREADY_A_MEMBER_OF_THIS_ORGANIZATION: "alreadyMember",
  USER_ACCOUNT_EXISTS_ELSEWHERE: "accountElsewhere",
};

/**
 * Invites someone into the caller's own company, through the same endpoint Team management uses.
 * auth-service refuses an account elsewhere only at the company's own email domains, so elsewhere
 * `invited` doesn't mean the address had no account.
 */
export function useInviteCompanyMember(
  options?: UseMutationOptions<CompanyInviteOutcome, Error, CompanyInvite, unknown>,
) {
  return useMutation({
    meta: SKIP_SENTRY_FOR_CLIENT_ERRORS,
    mutationKey: ["inviteCompanyMember"],
    ...options,
    mutationFn: async ({ email, role }: CompanyInvite): Promise<CompanyInviteOutcome> => {
      const { error } = await authClient.organization.inviteMember({ email, role });
      if (!error) return "invited";

      const outcome = error.code ? OUTCOME_BY_CODE[error.code] : undefined;
      // Swaps a pending member invite for an admin one; once it's admin, a repeat sends nothing.
      if (outcome === "alreadyInvited" && role === "admin") return replaceMemberInvite(email);
      if (outcome) return outcome;

      throw inviteFailure(error);
    },
  });
}

/** Resending keeps an invitation's role, so a pending member invite is cancelled and sent again. */
async function replaceMemberInvite(email: string): Promise<CompanyInviteOutcome> {
  const listed = await authClient.organization.listInvitations();
  if (listed.error) throw inviteFailure(listed.error);

  const pending = (listed.data ?? []).find(
    (invitation) =>
      invitation.status === "pending" &&
      invitation.email.toLowerCase() === email.toLowerCase() &&
      new Date(invitation.expiresAt) > new Date(),
  );
  if (!pending || pending.role === "admin") return "alreadyInvited";

  const cancelled = await authClient.organization.cancelInvitation({ invitationId: pending.id });
  if (cancelled.error) throw inviteFailure(cancelled.error);

  const reinvited = await authClient.organization.inviteMember({ email, role: "admin" });
  if (reinvited.error) throw inviteFailure(reinvited.error);
  return "invited";
}

function inviteFailure(error: { status?: number; message?: string }): Error {
  return new Error(betterAuthErrorText(error, "Could not send the invite"), { cause: error });
}
